<script setup>
  import { useAuthStore } from '~/stores/auth';
  import { useAlert } from '~/stores/alert';
  import { useVuelidate } from '@vuelidate/core';
  import { required, email, helpers } from '@vuelidate/validators';

  const runtimeConfig = useRuntimeConfig();
  const authStore = useAuthStore();
  const { showAlert } = useAlert();

  const isLoadding = ref(false);

  const rules = {
    email: {
      required: helpers.withMessage('Campo obrigatorio', required)
    },
    password: {
      required: helpers.withMessage('Campo obrigatorio', required)
    },
    login: {
      required: helpers.withMessage('Campo obrigatorio', required)
    }
  };

  const v$ = useVuelidate(rules, authStore.form.login);

  async function submit() {
    authStore.form.login.login = authStore.form.login.email;
    v$.value.$validate();

    if (!v$.value.$error) {
      try {
        isLoadding.value = true;

        await authStore.login();
        showAlert('success', 'Login feito com sucesso!');

        // Monta SDK Legitimuz logo após login
        if (runtimeConfig.public.LEGITIMUZ_API_TOKEN) {
          // Verifica se o SDK está disponível
          if (window.LegitimuzAntiFraude) {
            const sdkInstance = window.LegitimuzAntiFraude({
              apiURL: 'https://api.legitimuz.com',
              token: runtimeConfig.public.LEGITIMUZ_API_TOKEN,
              action: 'signin',
              enableRequestGeolocation: true,
              eventHandler: (event) => {
                console.log('LEGITIMUZ EVENT => ', event);
              }
            });

            sdkInstance.mount();
          }
        }
      } catch (err) {
        // Show the exact error message from the API
        showAlert('error', err.message);
      } finally {
        isLoadding.value = false;
      }
    }
  }
</script>

<template>
  <!-- Content -->
  <div class="flex w-full select-none flex-col items-center gap-y-6">
    <!-- Logo -->
    <div class="flex py-1 items-center justify-center">
      <BaseLogo class="max-w-24" />
    </div>

    <AuthCouponInfo />

    <!-- Form -->
    <div class="flex w-full flex-col gap-y-4">
      <div class="group relative">
        <p class="text-xs text-brand-primary" v-if="authStore.formErrors.login?.email">
          {{ authStore.formErrors.login?.email[0] }}
        </p>

        <BaseInput
          :id="'legitimuz-hydrate-cpf'"
          type="email"
          placeholder="Email ou CPF"
          name="email"
          @keyup.enter="submit"
          v-model="authStore.form.login.email"
          class="border-red-900 bg-red-600"
          :error="authStore.formErrors.login?.email || v$.email.$errors[0]?.$message"
        />
      </div>

      <div class="group relative">
        <p class="text-xs text-brand-primary" v-if="authStore.formErrors.login?.password">
          {{ authStore.formErrors.login?.password[0] }}
        </p>

        <BaseInput
          type="password"
          placeholder="Senha"
          name="password"
          v-model="authStore.form.login.password"
          class="border-red-900 bg-red-600"
          @keyup.enter="submit"
          :error="authStore.formErrors.login?.password || v$.password.$errors[0]?.$message"
        />
      </div>
      <p
        @click="authStore.modalOpen('recover')"
        class="cursor-pointer text-xs font-semibold text-bzMenuText hover:text-white"
      >
        Esqueceu a senha?
      </p>

      <BaseButton
        :id="'legitimuz-action-send-analisys'"
        variant="primary"
        @click="submit"
        :loading="isLoadding"
      >
        <p>Entrar</p>
      </BaseButton>
      <!-- OR separator -->
      <div class="relative flex h-10 items-center justify-center" v-if="false">
        <div class="h-0.5 w-full bg-separator"></div>
        <div class="absolute flex w-full flex-row items-center justify-center">
          <p class="bg-secondary p-2 text-sm font-bold text-bzMenuTextDark">or</p>
        </div>
      </div>

      <!-- Social Login -->
      <div class="flex flex-row justify-around" v-if="false">
        <div class="cursor-pointer rounded-md p-4 ring-1 ring-separator hover:bg-primary">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="h-6 w-6"
          >
            <path
              fill-rule="evenodd"
              d="M17.834 6.166a8.25 8.25 0 100 11.668.75.75 0 011.06 1.06c-3.807 3.808-9.98 3.808-13.788 0-3.808-3.807-3.808-9.98 0-13.788 3.807-3.808 9.98-3.808 13.788 0A9.722 9.722 0 0121.75 12c0 .975-.296 1.887-.809 2.571-.514.685-1.28 1.179-2.191 1.179-.904 0-1.666-.487-2.18-1.164a5.25 5.25 0 11-.82-6.26V8.25a.75.75 0 011.5 0V12c0 .682.208 1.27.509 1.671.3.401.659.579.991.579.332 0 .69-.178.991-.579.3-.4.509-.99.509-1.671a8.222 8.222 0 00-2.416-5.834zM15.75 12a3.75 3.75 0 10-7.5 0 3.75 3.75 0 007.5 0z"
              clip-rule="evenodd"
            />
          </svg>
        </div>

        <div class="cursor-pointer rounded-md p-4 ring-1 ring-separator hover:bg-primary">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="h-6 w-6"
          >
            <path
              fill-rule="evenodd"
              d="M17.834 6.166a8.25 8.25 0 100 11.668.75.75 0 011.06 1.06c-3.807 3.808-9.98 3.808-13.788 0-3.808-3.807-3.808-9.98 0-13.788 3.807-3.808 9.98-3.808 13.788 0A9.722 9.722 0 0121.75 12c0 .975-.296 1.887-.809 2.571-.514.685-1.28 1.179-2.191 1.179-.904 0-1.666-.487-2.18-1.164a5.25 5.25 0 11-.82-6.26V8.25a.75.75 0 011.5 0V12c0 .682.208 1.27.509 1.671.3.401.659.579.991.579.332 0 .69-.178.991-.579.3-.4.509-.99.509-1.671a8.222 8.222 0 00-2.416-5.834zM15.75 12a3.75 3.75 0 10-7.5 0 3.75 3.75 0 007.5 0z"
              clip-rule="evenodd"
            />
          </svg>
        </div>

        <div class="cursor-pointer rounded-md p-4 ring-1 ring-separator hover:bg-primary">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="h-6 w-6"
          >
            <path
              fill-rule="evenodd"
              d="M17.834 6.166a8.25 8.25 0 100 11.668.75.75 0 011.06 1.06c-3.807 3.808-9.98 3.808-13.788 0-3.808-3.807-3.808-9.98 0-13.788 3.807-3.808 9.98-3.808 13.788 0A9.722 9.722 0 0121.75 12c0 .975-.296 1.887-.809 2.571-.514.685-1.28 1.179-2.191 1.179-.904 0-1.666-.487-2.18-1.164a5.25 5.25 0 11-.82-6.26V8.25a.75.75 0 011.5 0V12c0 .682.208 1.27.509 1.671.3.401.659.579.991.579.332 0 .69-.178.991-.579.3-.4.509-.99.509-1.671a8.222 8.222 0 00-2.416-5.834zM15.75 12a3.75 3.75 0 10-7.5 0 3.75 3.75 0 007.5 0z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </div>

      <!-- Register step -->
      <p
        @click="authStore.modalOpen('register')"
        class="cursor-pointer text-center text-xs font-bold text-bzMenuText"
      >
        Você ainda não possui uma conta?
        <span class="text-brand-primary hover:text-brighten-brand-primary">Crie uma agora!</span>
      </p>
    </div>
  </div>
</template>
